import { useState } from "preact/hooks"
import { useRoute } from "wouter-preact"
import { AlertCircle, CheckCircle2, Loader2, Mail, ShieldCheck, Terminal } from "lucide-react"
import gmailLogo from "/img/integration/gmail.png"
import microsoftLogo from "/img/integration/microsoft.png"
import { CenterColumnContainer } from "@/components/LoginScreen.tsx"
import { Button } from "@/components/ui/button.tsx"
import { useAsyncEffect } from "@/utils"
import { copyToClipboard } from "@/utils/copy"
import {
  ConnectMode,
  PublicMailboxInvite,
  checkMailboxInvite,
  getPublicMailboxInvite,
  provider_display_name,
  startMailboxInvite,
} from "@/api/mailbox_invite"
import { powershell_setup_script } from "./app_only_setup"

/**
 * The page a brand's IT admin lands on. They have no MarginWiz login, so everything they need —
 * which mailbox, for whom, what we'll read, and what went wrong last time — has to be here.
 *
 * The mailbox is fixed by the link. Whatever account they sign in with, the callback connects this
 * mailbox or nothing.
 */
export function ConnectMailboxPage() {
  const [, params] = useRoute("/connect-mailbox/:id")
  const [invite, setInvite] = useState<PublicMailboxInvite | null>(null)
  const [loading, setLoading] = useState(true)
  const [starting, setStarting] = useState(false)
  const [checking, setChecking] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const just_connected = new URLSearchParams(window.location.search).get("status") === "connected"

  useAsyncEffect(async () => {
    if (!params?.id) {
      setError("This connect link is not valid.")
      setLoading(false)
      return
    }
    const res = await getPublicMailboxInvite(params.id)
    setLoading(false)
    if (!res.ok) {
      setError("We couldn't find this connect link. It may have been deleted — ask for a new one.")
      return
    }
    setInvite(res.value.data)
  }, [params?.id])

  async function start(mode: ConnectMode) {
    if (!invite) return
    setStarting(true)
    setError(null)
    const res = await startMailboxInvite(invite.id, mode)
    if (!res.ok) {
      setStarting(false)
      setError(res.error.message)
      return
    }
    window.location.href = res.value.data
  }

  /** Re-probes the mailbox after the admin's Exchange step. No consent round-trip needed. */
  async function check_again() {
    if (!invite) return
    setChecking(true)
    setError(null)
    const res = await checkMailboxInvite(invite.id)
    setChecking(false)
    if (!res.ok) {
      setError(res.error.message)
      return
    }
    setInvite(res.value.data)
  }

  if (loading) {
    return (
      <CenterColumnContainer>
        <div class="flex items-center justify-center gap-2 text-gray-500 py-6">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span class="text-sm">Loading…</span>
        </div>
      </CenterColumnContainer>
    )
  }

  if (!invite) {
    return (
      <CenterColumnContainer>
        <StatusMessage tone="error" title="This link isn't valid">
          {error ?? "Ask your MarginWiz contact for a new connect link."}
        </StatusMessage>
      </CenterColumnContainer>
    )
  }

  const is_microsoft = invite.integration_name === "microsoft"
  const logo = is_microsoft ? microsoftLogo : gmailLogo

  if (just_connected && invite.status === "connected") {
    return (
      <CenterColumnContainer>
        <StatusMessage tone="success" title={`${invite.email} is connected`}>
          <p>
            {invite.organization_name} can now see deduction backup that arrives in this mailbox. You
            can close this window.
          </p>
          {invite.connected_by_account && (
            <p class="text-gray-500 text-xs mt-2">Authorized by {invite.connected_by_account}.</p>
          )}
        </StatusMessage>
      </CenterColumnContainer>
    )
  }

  if (!invite.can_connect) {
    return (
      <CenterColumnContainer>
        <StatusMessage tone="error" title="This link is no longer active">
          {invite.status === "revoked"
            ? `${invite.organization_name} revoked this link. Ask them for a new one.`
            : `This link expired. Ask ${invite.organization_name} for a new one.`}
        </StatusMessage>
      </CenterColumnContainer>
    )
  }

  return (
    <CenterColumnContainer>
      <div class="space-y-6">
        <div class="flex items-center gap-3">
          <img src={logo} alt={provider_display_name(invite.integration_name)} class="h-6" />
          <span class="text-xs uppercase tracking-wide text-gray-400">
            {provider_display_name(invite.integration_name)}
          </span>
        </div>

        <div>
          <h1 class="text-xl font-semibold text-gray-900">Connect a mailbox to MarginWiz</h1>
          <p class="text-gray-500 text-sm mt-1">
            {invite.organization_name} asked to connect one mailbox so MarginWiz can read the
            deduction and remittance email that arrives in it.
          </p>
        </div>

        <div class="border border-gray-200 rounded-lg p-4 flex items-start gap-3">
          <Mail className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
          <div>
            <p class="text-xs uppercase tracking-wide text-gray-400">Mailbox</p>
            <p class="font-mono text-sm text-gray-900 break-all">{invite.email}</p>
            <p class="text-gray-500 text-xs mt-1">
              This link only connects this mailbox. If you sign in with a different account, we'll
              check whether it can open this mailbox — and connect nothing if it can't.
            </p>
          </div>
        </div>

        {invite.awaiting_mailbox_scope && invite.microsoft_app_id && (
          <AppOnlySetup invite={invite} appId={invite.microsoft_app_id} checking={checking} onCheck={check_again} />
        )}

        {invite.status === "connected" && (
          <StatusMessage tone="success" title="Already connected">
            This mailbox is connected
            {invite.connected_by_account ? ` (authorized by ${invite.connected_by_account})` : ""}. You
            only need to run this again if {invite.organization_name} asked you to re-authorize it.
          </StatusMessage>
        )}

        {invite.last_error && invite.status !== "connected" && !invite.awaiting_mailbox_scope && (
          <StatusMessage tone="error" title="The last attempt didn't connect">
            {invite.last_error}
          </StatusMessage>
        )}

        {error && (
          <StatusMessage tone="error" title="Something went wrong">
            {error}
          </StatusMessage>
        )}

        {is_microsoft && !invite.awaiting_mailbox_scope && (
          <div class="space-y-2">
            <Button className="w-full" disabled={starting} onClick={() => start("app_only")}>
              {starting ? "Redirecting…" : "Have your Microsoft admin grant access (recommended)"}
            </Button>
            <p class="text-gray-500 text-xs">
              No user account, password or MFA involved: a Microsoft 365 admin approves the
              MarginWiz app once, then limits it to {invite.email} in Exchange with two PowerShell
              commands we show you. This is the setup Microsoft recommends for services and it is
              unaffected by MFA policy changes.
            </p>
          </div>
        )}

        <div class="space-y-3">
          <Button
            className="w-full"
            variant={is_microsoft ? "outline" : "default"}
            disabled={starting}
            onClick={() => start("direct")}>
            {starting ? "Redirecting…" : `Sign in as ${invite.email}`}
          </Button>
          {is_microsoft && (
            <Button
              variant="outline"
              className="w-full"
              disabled={starting}
              onClick={() => start("delegated")}>
              It's a shared mailbox — sign in as an admin instead
            </Button>
          )}
        </div>

        {is_microsoft && (
          <p class="text-gray-500 text-xs">
            Signing in ties the connection to that account: if your tenant later requires MFA or
            the account is disabled, syncing stops until someone signs in again. Shared mailboxes
            usually can't sign in themselves; use the admin option and sign in with an account that
            has <strong>Full Access</strong> to {invite.email}, which we verify before connecting.
          </p>
        )}

        <div class="border-t border-gray-100 pt-4 space-y-2">
          <div class="flex items-start gap-2 text-xs text-gray-500">
            <ShieldCheck className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
            <div>
              <p class="font-medium text-gray-700">What MarginWiz gets</p>
              <p>
                Read access to mail in this one mailbox, and the ability to reply to deduction
                threads from it. No calendar, files, Teams or directory access, and nothing in any
                other mailbox.
              </p>
              <p class="mt-1">
                You sign in with {is_microsoft ? "Microsoft" : "Google"} — we never see your password,
                and access can be revoked at any time from your admin console.
              </p>
            </div>
          </div>
        </div>
      </div>
    </CenterColumnContainer>
  )
}

interface AppOnlySetupProps {
  invite: PublicMailboxInvite
  appId: string
  checking: boolean
  onCheck: () => void
}

/**
 * Consent is granted, the mailbox is not yet in scope. The admin has one Exchange step left, and
 * the assignment they make can take up to two hours to apply, so this stays on the page with a
 * re-check button rather than sending them through consent again.
 */
function AppOnlySetup({ invite, appId, checking, onCheck }: AppOnlySetupProps) {
  const script = powershell_setup_script(appId, invite.email)
  return (
    <div class="border border-amber-200 bg-amber-50 rounded-lg p-4 space-y-3">
      <div class="flex items-start gap-3">
        <Terminal className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
        <div class="text-sm text-amber-900">
          <p class="font-medium mb-1">One step left: allow the app to open this mailbox</p>
          <p>
            Your admin granted MarginWiz consent
            {invite.tenant_id ? ` in tenant ${invite.tenant_id}` : ""}. Exchange still has to be told
            which mailbox the app may read. Run this in Exchange Online PowerShell as an Exchange
            administrator, then check again. Exchange can take up to two hours to apply the change.
          </p>
        </div>
      </div>
      <pre class="text-xs bg-white border border-amber-200 rounded p-3 overflow-x-auto whitespace-pre text-gray-800">
        {script}
      </pre>
      <div class="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => copyToClipboard(script, "PowerShell copied to clipboard")}>
          Copy PowerShell
        </Button>
        <Button type="button" size="sm" disabled={checking} onClick={onCheck}>
          {checking ? "Checking…" : "Check access again"}
        </Button>
      </div>
      {invite.last_error && <p class="text-xs text-amber-800">{invite.last_error}</p>}
    </div>
  )
}

interface StatusMessageProps {
  tone: "success" | "error"
  title: string
  children: preact.ComponentChildren
}

function StatusMessage({ tone, title, children }: StatusMessageProps) {
  const success = tone === "success"
  const box = success ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"
  const heading = success ? "text-green-900" : "text-red-900"
  const body = success ? "text-green-800" : "text-red-800"
  const Icon = success ? CheckCircle2 : AlertCircle
  return (
    <div class={`border rounded-lg p-4 flex items-start gap-3 ${box}`}>
      <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${heading}`} />
      <div class={`text-sm ${body}`}>
        <p class={`font-medium mb-1 ${heading}`}>{title}</p>
        {children}
      </div>
    </div>
  )
}
